// SCR-STAFF-908 — دسترسی‌های پشتیبانی (US-PLT-081): درخواست‌های پشتیبانی Phoenix با دلیل و مدت (حداکثر ۴۸ ساعت)؛ مالک یا مدیر
// تأیید (با انتخاب صریح «شامل داده بالینی» فقط اگر درخواست شده باشد — AC-PLT-081.2)، رد یا هر زمان لغو می‌کند (AC-PLT-081.3).
// مجوز فعال SupportViewer فقط‌خواندنی است و همه اقدامات آن در Audit مستأجر ثبت می‌شود.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type SupportAccessGrantView, type SupportAccessStatus } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  ListPage,
  StatusChip,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { pltKeys, toCount, WriteNotice } from './common';

export const SCR_STAFF_908 = 'SCR-STAFF-908';
export const SUPPORT_ACCESS_PERMISSION = 'plt.support_access.approve';

export function supportStatusTone(status: SupportAccessStatus): StatusTone {
  switch (status) {
    case 'Approved':
      return 'active';
    case 'Requested':
      return 'pending';
    case 'Rejected':
    case 'Revoked':
      return 'no_show';
    default:
      return 'inactive';
  }
}

function ApproveDialog({ grant, onClose }: { grant: SupportAccessGrantView; onClose: () => void }) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [clinical, setClinical] = useState(false);
  const [note, setNote] = useState('');

  const approve = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/platform/support-access/{id}:approve', {
          params: { path: { id: grant.id } },
          body: { includesClinical: clinical, note: note.trim() || null },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pltKeys.supportAccess(session?.tenantId) });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={approve.isPending ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>{t('plt:support_access.approve_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>
            {t('plt:support_access.approve_message', {
              name: grant.requestedByName,
              hours: faDigits(toCount(grant.durationHours)),
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {grant.reason}
          </Typography>
          {grant.requestedClinical ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={clinical}
                  onChange={(event) => setClinical(event.target.checked)}
                  data-testid="approve-clinical"
                />
              }
              label={t('plt:support_access.include_clinical')}
            />
          ) : (
            <Alert severity="info">{t('plt:support_access.clinical_hidden')}</Alert>
          )}
          <TextField
            label={t('plt:support_access.note')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          {approve.error ? <ErrorState error={approve.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={approve.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => approve.mutate()}
          disabled={approve.isPending}
          data-testid="approve-confirm"
        >
          {t('plt:support_access.approve')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SupportAccessPage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  // تأیید فقط در حالت Full؛ رد و لغو در ReadOnly هم مجاز است (حفاظت از حریم مستأجر — AllowInReadOnly سرور).
  const write = useWriteAccess(SUPPORT_ACCESS_PERMISSION);
  const canApprove = write.canWrite;
  const canEnd = write.permitted;
  const [approving, setApproving] = useState<SupportAccessGrantView | null>(null);
  const [action, setAction] = useState<{ grant: SupportAccessGrantView; kind: 'reject' | 'revoke' } | null>(
    null,
  );

  const grants = useQuery({
    queryKey: pltKeys.supportAccess(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/support-access')),
  });

  const decide = useMutation({
    mutationFn: ({ grant, kind }: { grant: SupportAccessGrantView; kind: 'reject' | 'revoke' }) =>
      kind === 'reject'
        ? unwrap(
            tenantApi.POST('/api/v1/platform/support-access/{id}:reject', {
              params: { path: { id: grant.id } },
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/platform/support-access/{id}:revoke', {
              params: { path: { id: grant.id } },
            }),
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pltKeys.supportAccess(session?.tenantId) });
      setAction(null);
    },
  });

  const columns: DataTableColumn<SupportAccessGrantView>[] = [
    {
      id: 'requestedBy',
      headerKey: 'plt:support_access.columns.requested_by',
      cell: (row) => row.requestedByName,
    },
    { id: 'reason', headerKey: 'plt:support_access.columns.reason', cell: (row) => row.reason },
    {
      id: 'duration',
      headerKey: 'plt:support_access.columns.duration',
      cell: (row) => t('plt:support_access.hours', { value: faDigits(toCount(row.durationHours)) }),
    },
    {
      id: 'scope',
      headerKey: 'plt:support_access.columns.scope',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Chip
            size="small"
            variant="outlined"
            color={row.includesClinical ? 'warning' : 'default'}
            label={
              row.includesClinical
                ? t('plt:support_access.scope_clinical')
                : row.requestedClinical && row.status === 'Requested'
                  ? t('plt:support_access.scope_clinical_requested')
                  : t('plt:support_access.scope_read_only')
            }
          />
          {row.isEmergency && <Chip size="small" color="error" label={t('plt:support_access.emergency')} />}
        </Stack>
      ),
    },
    {
      id: 'status',
      headerKey: 'plt:support_access.columns.status',
      cell: (row) => (
        <StatusChip
          tone={supportStatusTone(row.status)}
          labelKey={`plt:support_access.status.${row.status}`}
        />
      ),
    },
    {
      id: 'expiresAt',
      headerKey: 'plt:support_access.columns.expires_at',
      cell: (row) =>
        row.endedAt ? (
          <JalaliDate value={row.endedAt} withTime />
        ) : row.expiresAt ? (
          <JalaliDate value={row.expiresAt} withTime />
        ) : (
          t('common:states.none')
        ),
    },
    {
      id: 'actions',
      headerKey: 'plt:branches.columns.actions',
      cell: (row) =>
        row.status === 'Requested' ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={() => setApproving(row)}
              disabled={!canApprove}
              data-testid={`grant-approve-${row.id}`}
            >
              {t('plt:support_access.approve')}
            </Button>
            <Button
              size="small"
              onClick={() => setAction({ grant: row, kind: 'reject' })}
              disabled={!canEnd}
              data-testid={`grant-reject-${row.id}`}
            >
              {t('plt:support_access.reject')}
            </Button>
          </Stack>
        ) : row.status === 'Approved' ? (
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={() => setAction({ grant: row, kind: 'revoke' })}
            disabled={!canEnd}
            data-testid={`grant-revoke-${row.id}`}
          >
            {t('plt:support_access.revoke')}
          </Button>
        ) : null,
    },
  ];

  const active = (grants.data?.items ?? []).filter((item) => item.status === 'Approved');
  return (
    <ListPage
      screenId={SCR_STAFF_908}
      titleKey="plt:support_access.title"
      subtitleKey="plt:support_access.subtitle"
      filters={
        <Stack spacing={1}>
          {!canApprove && <WriteNotice write={write} />}
          {active.length > 0 && (
            <Alert severity="warning" data-testid="support-access-active">
              {t('plt:support_access.active_notice', {
                count: active.length,
                value: faDigits(active.length),
              })}
            </Alert>
          )}
        </Stack>
      }
    >
      <DataTable
        captionKey="plt:support_access.caption"
        columns={columns}
        rows={grants.data?.items}
        getRowId={(row) => row.id}
        isLoading={grants.isPending}
        error={grants.error}
        onRetry={() => void grants.refetch()}
        emptyTitleKey="plt:support_access.empty"
      />
      {approving && <ApproveDialog grant={approving} onClose={() => setApproving(null)} />}
      <ConfirmDialog
        open={action !== null}
        titleKey={
          action?.kind === 'revoke' ? 'plt:support_access.revoke_title' : 'plt:support_access.reject_title'
        }
        messageKey={
          action?.kind === 'revoke'
            ? 'plt:support_access.revoke_message'
            : 'plt:support_access.reject_message'
        }
        confirmKey={action?.kind === 'revoke' ? 'plt:support_access.revoke' : 'plt:support_access.reject'}
        destructive
        busy={decide.isPending}
        error={decide.error}
        onConfirm={() => action && decide.mutate(action)}
        onCancel={() => {
          decide.reset();
          setAction(null);
        }}
      />
    </ListPage>
  );
}
